import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { CToast, CToastBody, CToastClose, CToaster } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle, cilWarning, cilXCircle, cilInfo } from '@coreui/icons'

type ToastColor = 'success' | 'danger' | 'warning' | 'info'

interface Toast {
  id: number
  message: string
  color: ToastColor
}

interface ToastContextType {
  showSuccess: (message: string) => void
  showError: (message: string) => void
  showWarning: (message: string) => void
  showInfo: (message: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

const TOAST_DURATION = 5000

const getIconForColor = (color: ToastColor) => {
  switch (color) {
    case 'success':
      return cilCheckCircle
    case 'danger':
      return cilXCircle
    case 'warning':
      return cilWarning
    case 'info':
      return cilInfo
  }
}

interface ToastProviderProps {
  children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, color: ToastColor) => {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message, color }])

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, TOAST_DURATION)
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showSuccess = useCallback(
    (message: string) => addToast(message, 'success'),
    [addToast]
  )
  const showError = useCallback(
    (message: string) => addToast(message, 'danger'),
    [addToast]
  )
  const showWarning = useCallback(
    (message: string) => addToast(message, 'warning'),
    [addToast]
  )
  const showInfo = useCallback(
    (message: string) => addToast(message, 'info'),
    [addToast]
  )

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showWarning, showInfo }}>
      {children}
      <CToaster placement="top-end" className="position-fixed p-3">
        {toasts.map((toast) => (
          <CToast
            key={toast.id}
            visible
            color={toast.color}
            className="text-white align-items-center"
          >
            <div className="d-flex">
              <CToastBody className="d-flex align-items-center">
                <CIcon
                  icon={getIconForColor(toast.color)}
                  className="flex-shrink-0 me-2"
                  width={20}
                  height={20}
                />
                {toast.message}
              </CToastBody>
              <CToastClose
                className="me-2 m-auto"
                white
                onClick={() => removeToast(toast.id)}
              />
            </div>
          </CToast>
        ))}
      </CToaster>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
